import { z } from "zod";
import { IdSchema, Sha256Schema } from "./primitives.js";
/**
 * Chart-history contracts: the governed append-one writers behind
 * `POST /v1/patients/:id/{problems|medications|allergies}` and the Postgres
 * functions `api.record_patient_{problem,medication,allergy}`.
 *
 * Each input schema mirrors the `p_*` allow-list of its RPC EXACTLY (strict
 * keys, same enums, same lengths) so the agent-api fails fast with a 400 before
 * a doomed round trip, the Supabase adapter re-parses the identical shape, and
 * clinical-web builds payloads that cannot drift. One definition, three
 * consumers — the hand-maintained copies were retired by 20260904014447.
 *
 * Problems are PAST MEDICAL HISTORY (owner decision 2026-09-03): free text
 * first, an OPTIONAL coding pair, a `condition | surgical` category, and an
 * optional note.
 */
export const ChartSourceKindSchema = z.enum([
    "patient_reported",
    "clinician_asserted",
    "clinician_prescribed",
    "laboratory_result",
    "medication_reconciliation",
    "external_document",
]);
export const ChartCodeSystemSchema = z.enum(["ICD-10-CA", "ICD-10-CM", "SNOMED-CT", "local"]);
/**
 * Coding systems for allergy, medication and observation codes (PC-4,
 * 20260925123232). A system never travels without its code. A code without a
 * system was recorded before systems existed: the writers accept it only as an
 * unchanged echo onto the next version of the same entry, which keeps the
 * stored system; every other code must name one.
 */
export const AllergyCodeSystemSchema = z.enum([
    "SNOMED-CT",
    "RxNorm",
    "DIN",
    "FDB_HIC_SEQN",
    "FDB_DAM_ALRGN_GRP",
    "local",
]);
export const MedicationCodeSystemSchema = z.enum(["DIN", "RxNorm", "FDB_MEDID", "local"]);
export const ObservationCodeSystemSchema = z.enum(["LOINC", "local"]);
/**
 * The write-side code rule for allergies and medications: a system needs a
 * code, and a code without a system is only accepted on an existing entry
 * (`existingKey` present), where the writer checks it is the stored code.
 */
export function codeWithSystem(codeKey, systemKey, existingKey) {
    return (value, context) => {
        if (value[systemKey] !== undefined && value[codeKey] === undefined) {
            context.addIssue({
                code: "custom",
                path: [codeKey],
                message: "A code system needs a code",
            });
        }
        else if (value[codeKey] !== undefined &&
            value[systemKey] === undefined &&
            value[existingKey] === undefined) {
            context.addIssue({
                code: "custom",
                path: [systemKey],
                message: "Choose the system for this code",
            });
        }
    };
}
export const ProblemCategorySchema = z.enum(["condition", "surgical"]);
export const ProblemClinicalStatusSchema = z.enum(["active", "inactive", "resolved"]);
/**
 * Mirrors the p_problem allow-list of api.record_patient_problem exactly.
 * `codeSystem` and `conditionCode` are optional but must travel together.
 */
export const RecordPatientProblemInputSchema = z
    .object({
    patientId: IdSchema,
    codeSystem: ChartCodeSystemSchema.optional(),
    conditionCode: z.string().min(1).max(80).optional(),
    displayName: z.string().min(1).max(240),
    clinicalStatus: ProblemClinicalStatusSchema,
    onsetDate: z.string().date().optional(),
    category: ProblemCategorySchema.optional(),
    note: z.string().min(1).max(2000).optional(),
    supersedesId: IdSchema.optional(),
    sourceKind: ChartSourceKindSchema.optional(),
})
    .strict()
    .superRefine((input, context) => {
    if ((input.codeSystem === undefined) !== (input.conditionCode === undefined)) {
        context.addIssue({
            code: "custom",
            path: [input.codeSystem === undefined ? "codeSystem" : "conditionCode"],
            message: "codeSystem and conditionCode must be provided together",
        });
    }
});
/**
 * Mirrors the p_medication allow-list of api.record_patient_medication exactly.
 * `medicationCodeSystem` travels with `medicationCode` (see codeWithSystem).
 */
export const RecordPatientMedicationInputSchema = z
    .object({
    patientId: IdSchema,
    medicationCode: z.string().min(1).max(80).optional(),
    medicationCodeSystem: MedicationCodeSystemSchema.optional(),
    medicationDisplay: z.string().min(1).max(240),
    sig: z.string().min(1).max(500).optional(),
    status: z.enum(["active", "inactive", "completed", "unknown"]),
    supersedesId: IdSchema.optional(),
    sourceKind: ChartSourceKindSchema.optional(),
})
    .strict()
    .superRefine(codeWithSystem("medicationCode", "medicationCodeSystem", "supersedesId"));
/**
 * Mirrors the p_allergy allow-list of api.record_patient_allergy exactly.
 * `substanceCodeSystem` travels with `substanceCode` (see codeWithSystem).
 */
export const RecordPatientAllergyInputSchema = z
    .object({
    patientId: IdSchema,
    substanceCode: z.string().min(1).max(80).optional(),
    substanceCodeSystem: AllergyCodeSystemSchema.optional(),
    substanceDisplay: z.string().min(1).max(240),
    reaction: z.string().min(1).max(500).optional(),
    severity: z.enum(["mild", "moderate", "severe", "unknown"]).optional(),
    clinicalStatus: z.enum(["active", "inactive", "resolved"]),
    supersedesId: IdSchema.optional(),
    sourceKind: ChartSourceKindSchema.optional(),
})
    .strict()
    .superRefine(codeWithSystem("substanceCode", "substanceCodeSystem", "supersedesId"));
/**
 * Receipt returned by every chart-history writer: `{ version, contentHash }`
 * plus the writer-specific id key (`problemId` | `medicationId` | `allergyId`).
 *
 * The problem writer dedupes against the patient's current heads
 * (20260906212333): `created: false` means an active head already carried the
 * code (or the uncoded display) and nothing was recorded — the receipt is that
 * head; `reactivated: true` means a resolved/inactive head was superseded by
 * the recorded entry and that entry is active. Writers that never dedupe — and
 * the problem writer when the input names `supersedesId` — omit both flags.
 */
export const ChartRecordReceiptSchema = z
    .object({
    version: z.coerce.number().int().positive(),
    contentHash: Sha256Schema,
    created: z.boolean().optional(),
    reactivated: z.boolean().optional(),
})
    .passthrough();
//# sourceMappingURL=chart-history.js.map